import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import DemoBanner from '../components/DemoBanner';
import {
  Form, Table, Alert, Container, Row, Col, InputGroup, Modal, Button, Card
} from 'react-bootstrap';
import CreatableSelect from 'react-select/creatable';
import { useAuth } from '../context/AuthContext';
import {
  fetchBonusConfigs, fetchCargos, saveBonusConfig, deleteBonusConfig, toggleBonusStatus,
  fetchEtapasBonus, saveEtapaBonus, updateEtapaBonus, deleteEtapaBonus, toggleEtapaStatus
} from '../utils/api';
import { DICT } from '../utils/dictionary';

// --- LÓGICA WHITE LABEL ---
const HEADER_BANNER = process.env.REACT_APP_BANNER_CONFIG || "/client_assets/finger_athos.png";

// --- MOCK DATA (Modo Demonstração) ---
const DEMO_CARGOS = ['EDITOR', 'ROTEIRISTA', 'THUMBMAKER', 'SOCIAL MEDIA'];
const DEMO_CONFIG_INIT = [
  { id: 1, cargo: 'EDITOR', tipo_video: 'LONGO', tier: 1, views_minimas: 100000, valor_bonus: 50.00, dias_maximos: 30, ativo: true },
  { id: 2, cargo: 'EDITOR', tipo_video: 'LONGO', tier: 2, views_minimas: 500000, valor_bonus: 200.00, dias_maximos: 30, ativo: true },
  { id: 3, cargo: 'ROTEIRISTA', tipo_video: 'SHORT', tier: 1, views_minimas: 1000000, valor_bonus: 100.00, dias_maximos: 15, ativo: true },
];
const DEMO_ETAPAS_INIT = [
  { id: 1, etapa: 'PESQUISA', percentual: 30, cargo: 'ROTEIRISTA', tipo_video: 'longo', ativo: true },
  { id: 2, etapa: 'ESCRITA', percentual: 70, cargo: 'ROTEIRISTA', tipo_video: 'longo', ativo: true }
];

const Configuracoes = () => {
  const { logout, isDemo, token } = useAuth();
  const navigate = useNavigate();

  // --- ISOLAMENTO DE DOMÍNIO ---
  const CARGOS_COM_ETAPAS = DICT.CARGOS_ETAPAS;
  const tipos = DICT.TIPOS_CONTEUDO;
  const labelTipoVideo = DICT.FORMAT_LABEL;

  const [bonusConfig, setBonusConfig] = useState([]);
  const [cargos, setCargos] = useState([]);
  const [filtroCargo, setFiltroCargo] = useState('');
  const [filtroTipo, setFiltroTipo] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [formData, setFormData] = useState({
    cargo: '', tipo_video: '', tiers: 1,
    configuracoes: [{ id: null, tier: 1, views: '', unidade: 'Mil', valor_bonus: '', dias_maximos: '' }]
  });

  const [etapasBonus, setEtapasBonus] = useState([]);
  const [demoEtapasAll, setDemoEtapasAll] = useState(DEMO_ETAPAS_INIT);
  const [showEtapasSection, setShowEtapasSection] = useState(false);

  // Modais
  const [showDeleteTierModal, setShowDeleteTierModal] = useState(false);
  const [configToDelete, setConfigToDelete] = useState(null);
  const [showEtapaModal, setShowEtapaModal] = useState(false);
  const [etapaParaEditar, setEtapaParaEditar] = useState(null);
  const [showDeleteEtapaModal, setShowDeleteEtapaModal] = useState(false);
  const [etapaParaDeletar, setEtapaParaDeletar] = useState(null);

  const unidadeMultiplicador = { Mil: 1000, Milhão: 1000000, Bilhão: 1000000000 };

  // Busca inicial de dados
  useEffect(() => {
    const carregarDados = async () => {
      if (isDemo) {
        setBonusConfig(DEMO_CONFIG_INIT);
        setCargos(DEMO_CARGOS.map(cargo => ({ label: cargo, value: cargo })));
        return;
      }

      try {
        const [configData, cargosData] = await Promise.all([
          fetchBonusConfigs(token, navigate, logout),
          fetchCargos(token, navigate, logout)
        ]);
        setBonusConfig(configData);
        setCargos(cargosData.map(cargo => ({ label: cargo, value: cargo })));
      } catch (e) {
        setError(e.message);
      }
    };
    carregarDados();
  }, [navigate, logout, token, isDemo]);

  const buscarEtapas = useCallback(async (cargo, tipo) => {
    if (cargo && tipo) {
      if (isDemo) {
        setEtapasBonus(demoEtapasAll.filter(e => e.cargo === cargo && e.tipo_video.toLowerCase() === tipo.toLowerCase()));
        return;
      }
      try {
        const data = await fetchEtapasBonus(token, cargo, tipo);
        setEtapasBonus(data);
      } catch (e) {
        setError('Erro ao buscar etapas: ' + e.message);
        setEtapasBonus([]);
      }
    } else {
      setEtapasBonus([]);
    }
  }, [token, isDemo, demoEtapasAll]);

  useEffect(() => {
    const cargoSelecionado = formData.cargo?.toUpperCase();
    const tipoSelecionado = formData.tipo_video;

    if (CARGOS_COM_ETAPAS.includes(cargoSelecionado) && tipoSelecionado) {
      setShowEtapasSection(true);
      buscarEtapas(cargoSelecionado, tipoSelecionado);
    } else {
      setShowEtapasSection(false);
      setEtapasBonus([]);
    }
  }, [formData.cargo, formData.tipo_video, buscarEtapas, CARGOS_COM_ETAPAS]);

  const resetForm = () => {
    setFormData({
      cargo: '', tipo_video: '', tiers: 1,
      configuracoes: [{ id: null, tier: 1, views: '', unidade: 'Mil', valor_bonus: '', dias_maximos: '' }]
    });
    setEtapasBonus([]);
    setShowEtapasSection(false);
  };

  const formatCurrency = val => {
    const cents = parseInt(String(val).replace(/[^\d]/g, '') || '0');
    return (cents / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
  };

  const handleChange = ({ target: { name, value } }) => {
    if (name === 'tiers') {
      const qtd = parseInt(value);
      if (!qtd || qtd < 1) return;
      const configuracoes = Array.from({ length: qtd }, (_, i) => ({
        id: null, tier: i + 1, views: '', unidade: 'Mil', valor_bonus: '', dias_maximos: ''
      }));
      setFormData(p => ({ ...p, tiers: value, configuracoes }));
    } else {
      setFormData(p => ({ ...p, [name]: value }));
    }
  };

  const handleConfigChange = (i, campo, val) => {
    const novas = [...formData.configuracoes];
    if (campo === 'valor_bonus') val = val.replace(/[^\d]/g, '');
    novas[i][campo] = val;
    setFormData(p => ({ ...p, configuracoes: novas }));
  };

  const handleSubmitTiers = async e => {
    e.preventDefault();
    setError(''); setSuccess('');
    if (!formData.cargo || !formData.tipo_video) return setError(`Preencha o cargo e o ${labelTipoVideo.toLowerCase()}.`);

    if (isDemo) {
      const novasConfigs = formData.configuracoes.map(c => ({
        id: c.id || Math.floor(Math.random() * 1000),
        cargo: formData.cargo.toUpperCase(),
        tipo_video: formData.tipo_video,
        tier: parseInt(c.tier),
        views_minimas: parseInt(c.views || '0') * (unidadeMultiplicador[c.unidade] || 1000),
        valor_bonus: parseFloat(((parseInt(c.valor_bonus || '0') || 0) / 100).toFixed(2)),
        dias_maximos: parseInt(c.dias_maximos || '0'),
        ativo: true
      }));
      setBonusConfig(prev => {
        const ids = novasConfigs.map(c => c.id);
        const filtered = prev.filter(cfg => !ids.includes(cfg.id) && !(novasConfigs.find(nc => !nc.id && nc.cargo === cfg.cargo && nc.tipo_video === cfg.tipo_video && nc.tier === cfg.tier)));
        return [...filtered, ...novasConfigs];
      });
      setSuccess('[MODO DEMO] Tiers salvas localmente!');
      resetForm();
      return;
    }

    try {
      for (const c of formData.configuracoes) {
        const payload = {
          cargo: formData.cargo.toUpperCase(),
          tipo_video: formData.tipo_video,
          tier: parseInt(c.tier),
          views_minimas: parseInt(c.views || '0') * (unidadeMultiplicador[c.unidade] || 1000),
          valor_bonus: parseFloat(((parseInt(c.valor_bonus || '0') || 0) / 100).toFixed(2)),
          dias_maximos: parseInt(c.dias_maximos || '0'),
          id: c.id || undefined
        };
        await saveBonusConfig(token, navigate, logout, payload);
      }
      const atualizados = await fetchBonusConfigs(token, navigate, logout);
      setBonusConfig(atualizados);
      setSuccess('Tiers salvas com sucesso!');
      resetForm();
    } catch (e) { setError('Erro ao salvar tiers: ' + e.message); }
  };

  const handleEditTiers = (cargo, tipo) => {
    const config = bonusConfig.filter(c => c.cargo === cargo && c.tipo_video === tipo);
    if (!config.length) return;
    const configuracoes = config.map(c => ({
      id: c.id, tier: c.tier,
      views: c.views_minimas / 1000, unidade: 'Mil',
      valor_bonus: (c.valor_bonus * 100).toFixed(0),
      dias_maximos: c.dias_maximos
    }));
    setFormData({ cargo, tipo_video: tipo, tiers: configuracoes.length, configuracoes });
    window.scrollTo(0, 0);
  };

  const confirmDeleteTier = async () => {
    if (isDemo) {
      setBonusConfig(prev => prev.filter(c => c.id !== configToDelete));
      setSuccess('[MODO DEMO] Tier excluída!');
      setShowDeleteTierModal(false); setConfigToDelete(null);
      return;
    }

    try {
      await deleteBonusConfig(token, navigate, logout, configToDelete);
      setBonusConfig(bonusConfig.filter(c => c.id !== configToDelete));
      setSuccess('Meta de tier excluída com sucesso!');
    } catch (e) { setError(e.message); }
    finally { setShowDeleteTierModal(false); setConfigToDelete(null); }
  };

  const toggleAtivoTier = async (id) => {
    if (isDemo) {
      setBonusConfig(prev => prev.map(c => c.id === id ? { ...c, ativo: !c.ativo } : c));
      return;
    }
    const config = bonusConfig.find(c => c.id === id);
    try {
      await toggleBonusStatus(token, id, !config.ativo);
      setBonusConfig(bonusConfig.map(c => c.id === id ? { ...c, ativo: !c.ativo } : c));
    } catch (e) { setError('Erro ao alternar status da meta.'); }
  };

  // --- Funções para Etapas ---
  const handleSaveEtapa = async (etapaData) => {
    if (!formData.cargo) {
      setError("Selecione um Cargo antes de adicionar ou editar uma etapa.");
      setShowEtapaModal(false);
      return;
    }

    const payload = {
      id: etapaData.id,
      etapa: etapaData.etapa,
      percentual: parseInt(etapaData.percentual),
      cargo: formData.cargo.toUpperCase(),
      tipo_video: etapaData.tipo_video.toLowerCase()
    };

    if (isDemo) {
      if (payload.id) {
        setDemoEtapasAll(prev => prev.map(e => e.id === payload.id ? { ...e, ...payload } : e));
      } else {
        const novaEtapa = { ...payload, id: Math.floor(Math.random() * 1000), ativo: true };
        setDemoEtapasAll(prev => [...prev, novaEtapa]);
      }
      buscarEtapas(formData.cargo, formData.tipo_video);
      setSuccess('[MODO DEMO] Etapa salva localmente!');
      setShowEtapaModal(false); setEtapaParaEditar(null);
      return;
    }

    try {
      if (payload.id) {
        await updateEtapaBonus(token, payload.id, payload);
      } else {
        await saveEtapaBonus(token, payload);
      }
      await buscarEtapas(formData.cargo, formData.tipo_video);
      setSuccess('Etapa salva com sucesso!');
    } catch (e) {
      setError(`Erro ao salvar etapa: ${e.message}`);
    } finally {
      setShowEtapaModal(false);
      setEtapaParaEditar(null);
    }
  };

  const confirmDeleteEtapa = async () => {
    if (isDemo) {
      setDemoEtapasAll(prev => prev.filter(e => e.id !== etapaParaDeletar.id));
      setEtapasBonus(prev => prev.filter(e => e.id !== etapaParaDeletar.id));
      setSuccess('[MODO DEMO] Etapa excluída!');
      setShowDeleteEtapaModal(false); setEtapaParaDeletar(null);
      return;
    }

    try {
      await deleteEtapaBonus(token, etapaParaDeletar.id);
      setEtapasBonus(etapasBonus.filter(e => e.id !== etapaParaDeletar.id));
      setSuccess('Etapa excluída com sucesso!');
    } catch (e) {
      setError('Erro ao excluir etapa: ' + e.message);
    } finally {
      setShowDeleteEtapaModal(false);
      setEtapaParaDeletar(null);
    }
  };

  const toggleAtivoEtapa = async (id) => {
    if (isDemo) {
      setDemoEtapasAll(prev => prev.map(e => e.id === id ? { ...e, ativo: !e.ativo } : e));
      setEtapasBonus(prev => prev.map(e => e.id === id ? { ...e, ativo: !e.ativo } : e));
      return;
    }

    const etapa = etapasBonus.find(e => e.id === id);
    try {
      const etapaAtualizada = await toggleEtapaStatus(token, id, !etapa.ativo);
      setEtapasBonus(etapasBonus.map(e => (e.id === id ? etapaAtualizada : e)));
      setSuccess('Status da etapa alterado com sucesso!');
    } catch (e) {
      setError(`Erro ao alternar status da etapa: ${e.message}`);
    }
  };

  const filtrado = bonusConfig.filter(c =>
    (!filtroCargo || c.cargo.toUpperCase() === filtroCargo) &&
    (!filtroTipo || c.tipo_video === filtroTipo)
  );

  return (
    <Container fluid className="main-content">
      {isDemo && <DemoBanner />}
      <Row className="align-items-center mb-4">
        <Col xs={12} md={6}>
          <h2 style={{ color: 'var(--brand-secondary)' }}>
            <i className="bi bi-gear" style={{ color: 'var(--brand-primary)' }}></i> Configurações Tiers
          </h2>
        </Col>
        <Col xs={12} md={6} className="text-md-end d-none d-md-block">
          <img src={HEADER_BANNER} alt="Decoração Configurações" style={{ maxWidth: 150, height: 'auto', objectFit: 'contain' }} onError={(e) => { e.target.style.display = 'none'; }} />
        </Col>
      </Row>

      {error && <Alert variant="danger" onClose={() => setError('')} dismissible>{error}</Alert>}
      {success && <Alert variant="success" onClose={() => setSuccess('')} dismissible>{success}</Alert>}

      <Card className="mb-4 border-0 shadow-sm">
        <Card.Header style={{ backgroundColor: 'var(--brand-secondary)', color: 'var(--brand-text-on-dark)' }}>
          <h5 className="mb-0"><i className="bi bi-plus-circle me-2"></i> Cadastrar / Editar Tiers</h5>
        </Card.Header>
        <Card.Body>
          <Form onSubmit={handleSubmitTiers}>
            <Row className="mb-4">
              <Col md={4} className="mb-3 mb-md-0">
                <Form.Label className="text-muted small">Cargo</Form.Label>
                <CreatableSelect
                  isClearable
                  options={cargos}
                  onChange={o => setFormData(p => ({ ...p, cargo: o ? o.value.toUpperCase() : '' }))}
                  value={formData.cargo ? { label: formData.cargo, value: formData.cargo } : null}
                  placeholder="Selecione ou crie um cargo..."
                />
              </Col>
              <Col md={4} className="mb-3 mb-md-0">
                <Form.Label className="text-muted small">{labelTipoVideo}</Form.Label>
                <Form.Select name="tipo_video" value={formData.tipo_video} onChange={handleChange} required>
                  <option value="">Selecione...</option>
                  {tipos.map(t => <option key={t} value={t}>{t}</option>)}
                </Form.Select>
              </Col>
              <Col md={4}>
                <Form.Label className="text-muted small">Quantidade de Tiers</Form.Label>
                <Form.Control type="number" name="tiers" value={formData.tiers} onChange={handleChange} min={1} />
              </Col>
            </Row>

            {formData.configuracoes.map((cfg, i) => (
              <Card key={i} className="mb-3 border-light" style={{ backgroundColor: 'var(--brand-bg-light)' }}>
                <Card.Body className="p-3">
                  <Row className="align-items-center">
                    <Col md={1} className="mb-2 mb-md-0">
                      <strong style={{ color: 'var(--brand-primary)' }}>Tier {cfg.tier}</strong>
                    </Col>
                    <Col md={3} className="mb-2 mb-md-0">
                      <Form.Label className="small mb-1">Métrica Mínima</Form.Label>
                      <InputGroup size="sm">
                        <Form.Control type="number" value={cfg.views} onChange={e => handleConfigChange(i, 'views', e.target.value)} required />
                        <Form.Select value={cfg.unidade} onChange={e => handleConfigChange(i, 'unidade', e.target.value)}>
                          <option>Mil</option><option>Milhão</option><option>Bilhão</option>
                        </Form.Select>
                      </InputGroup>
                    </Col>
                    <Col md={4} className="mb-2 mb-md-0">
                      <Form.Label className="small mb-1">Valor do Bônus</Form.Label>
                      <InputGroup size="sm">
                        <InputGroup.Text>R$</InputGroup.Text>
                        <Form.Control value={formatCurrency(cfg.valor_bonus)} onChange={e => handleConfigChange(i, 'valor_bonus', e.target.value)} required />
                      </InputGroup>
                    </Col>
                    <Col md={4}>
                      <Form.Label className="small mb-1">Prazo Máx. (dias úteis)</Form.Label>
                      <Form.Control size="sm" type="number" value={cfg.dias_maximos} onChange={e => handleConfigChange(i, 'dias_maximos', e.target.value)} required />
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
            ))}
            <div className="text-end mt-3">
              <Button type="button" variant="outline-secondary" className="me-2" onClick={resetForm}>Limpar</Button>
              <Button type="submit" variant="primary"><i className="bi bi-save me-1"></i> Salvar Tiers</Button>
            </div>
          </Form>

          {showEtapasSection && (
            <>
              <hr className="my-4" />
              <h5 style={{ color: 'var(--brand-secondary)' }}><i className="bi bi-diagram-3 me-2" style={{ color: 'var(--brand-primary)' }}></i> Bônus Fracionado por Etapa ({formData.cargo})</h5>
              <p className="text-muted small">Defina o percentual de resgate que cada fase concluída representa para este perfil de colaborador.</p>

              <Table striped hover responsive className="align-middle border mt-3">
                <thead className="bg-light">
                  <tr><th>Fase / Etapa</th><th>Peso (%)</th><th>Status</th><th>Ações</th></tr>
                </thead>
                <tbody>
                  {etapasBonus.map(e => (
                    <tr key={e.id}>
                      <td className="fw-bold">{e.etapa}</td>
                      <td>{e.percentual}%</td>
                      <td>
                        <Button size="sm" variant={e.ativo ? 'success' : 'secondary'} onClick={() => toggleAtivoEtapa(e.id)}>
                          {e.ativo ? <><i className="bi bi-check-circle me-1" /> Ativa</> : <><i className="bi bi-slash-circle me-1" /> Inativa</>}
                        </Button>
                      </td>
                      <td>
                        <Button size="sm" variant="outline-primary" className="me-2" onClick={() => { setEtapaParaEditar(e); setShowEtapaModal(true); }}>
                          <i className="bi bi-pencil"></i>
                        </Button>
                        <Button size="sm" variant="outline-danger" onClick={() => { setEtapaParaDeletar(e); setShowDeleteEtapaModal(true); }}>
                          <i className="bi bi-trash"></i>
                        </Button>
                      </td>
                    </tr>
                  ))}
                  {etapasBonus.length === 0 && (
                    <tr><td colSpan="4" className="text-center text-muted py-3">Nenhuma etapa configurada para este cargo e formato.</td></tr>
                  )}
                </tbody>
              </Table>
              <Button variant="outline-primary" size="sm" onClick={() => { setEtapaParaEditar(null); setShowEtapaModal(true); }}>
                <i className="bi bi-plus-circle me-1"></i> Adicionar Nova Etapa
              </Button>
            </>
          )}
        </Card.Body>
      </Card>

      <Card className="border-0 shadow-sm">
        <Card.Body className="p-4">
          <h5 className="mb-4" style={{ color: 'var(--brand-secondary)' }}><i className="bi bi-list-ul me-2"></i> Tiers Cadastradas no Sistema</h5>
          <Row className="mb-4">
            <Col md={4} className="mb-2 mb-md-0">
              <Form.Label className="text-muted small">Filtrar por Cargo</Form.Label>
              <CreatableSelect isClearable options={cargos} onChange={o => setFiltroCargo(o ? o.value.toUpperCase() : '')} value={filtroCargo ? { label: filtroCargo, value: filtroCargo } : null} />
            </Col>
            <Col md={4}>
              <Form.Label className="text-muted small">Filtrar por {labelTipoVideo}</Form.Label>
              <Form.Select value={filtroTipo} onChange={e => setFiltroTipo(e.target.value)}>
                <option value="">Todos os formatos</option>
                {tipos.map(t => <option key={t}>{t}</option>)}
              </Form.Select>
            </Col>
          </Row>

          <div className="table-responsive">
            <Table hover className="align-middle">
              <thead style={{ backgroundColor: 'var(--brand-secondary)', color: 'var(--brand-text-on-dark)' }}>
                <tr><th className="border-0">Cargo</th><th className="border-0">Formato</th><th className="border-0">Tier</th><th className="border-0">Métrica</th><th className="border-0">Valor</th><th className="border-0">Status</th><th className="border-0">Ações</th></tr>
              </thead>
              <tbody>
                {filtrado.map(cfg => (
                  <tr key={cfg.id}>
                    <td className="fw-bold">{cfg.cargo}</td><td>{cfg.tipo_video}</td><td>{cfg.tier}</td>
                    <td>{cfg.views_minimas.toLocaleString('pt-BR')}</td>
                    <td className="fw-bold" style={{ color: 'var(--brand-primary)' }}>{formatCurrency((cfg.valor_bonus * 100).toString())}</td>
                    <td>
                      <Button size="sm" variant={cfg.ativo ? 'success' : 'secondary'} onClick={() => toggleAtivoTier(cfg.id)}>
                        {cfg.ativo ? 'Ativo' : 'Inativo'}
                      </Button>
                    </td>
                    <td>
                      <Button size="sm" variant="outline-primary" className="me-2" onClick={() => handleEditTiers(cfg.cargo, cfg.tipo_video)}><i className="bi bi-pencil"></i></Button>
                      <Button size="sm" variant="outline-danger" onClick={() => { setConfigToDelete(cfg.id); setShowDeleteTierModal(true); }}><i className="bi bi-trash"></i></Button>
                    </td>
                  </tr>
                ))}
                {filtrado.length === 0 && <tr><td colSpan="7" className="text-center py-4 text-muted">Nenhuma configuração encontrada.</td></tr>}
              </tbody>
            </Table>
          </div>
        </Card.Body>
      </Card>

      <Modal show={showDeleteTierModal} onHide={() => setShowDeleteTierModal(false)} centered>
        <Modal.Header closeButton><Modal.Title>Confirmar Exclusão</Modal.Title></Modal.Header>
        <Modal.Body>Deseja realmente excluir esta meta de tier do sistema?</Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => setShowDeleteTierModal(false)}>Cancelar</Button>
          <Button variant="danger" onClick={confirmDeleteTier}>Excluir Definitivamente</Button>
        </Modal.Footer>
      </Modal>

      {showEtapaModal && <ModalEtapa
        show={showEtapaModal}
        handleClose={() => { setShowEtapaModal(false); setEtapaParaEditar(null); }}
        handleSave={handleSaveEtapa}
        etapaInicial={etapaParaEditar}
        tipos={tipos}
        labelTipoVideo={labelTipoVideo}
      />}

      <Modal show={showDeleteEtapaModal} onHide={() => setShowDeleteEtapaModal(false)} centered>
        <Modal.Header closeButton><Modal.Title>Excluir Etapa</Modal.Title></Modal.Header>
        <Modal.Body>Deseja remover a etapa <strong>{etapaParaDeletar?.etapa}</strong> desta configuração?</Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => setShowDeleteEtapaModal(false)}>Cancelar</Button>
          <Button variant="danger" onClick={confirmDeleteEtapa}>Excluir Etapa</Button>
        </Modal.Footer>
      </Modal>

    </Container>
  );
};

const ModalEtapa = ({ show, handleClose, handleSave, etapaInicial, tipos, labelTipoVideo }) => {
  const [tipoVideo, setTipoVideo] = useState('LONGO');
  const [etapa, setEtapa] = useState('');
  const [percentual, setPercentual] = useState('');

  useEffect(() => {
    if (etapaInicial) {
      setEtapa(etapaInicial.etapa || '');
      setPercentual(etapaInicial.percentual || '');
      setTipoVideo(etapaInicial.tipo_video ? etapaInicial.tipo_video.toUpperCase() : 'LONGO');
    } else {
      setEtapa('');
      setPercentual('');
      setTipoVideo('LONGO');
    }
  }, [etapaInicial, show]);

  const onSave = () => {
    handleSave({ id: etapaInicial?.id, etapa, percentual, tipo_video: tipoVideo });
  };

  return (
    <Modal show={show} onHide={handleClose} backdrop="static" centered>
      <Modal.Header closeButton>
        <Modal.Title>{etapaInicial ? 'Editar Fase Fracionada' : 'Nova Fase Fracionada'}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form onSubmit={(e) => { e.preventDefault(); onSave(); }}>
          <Form.Group className="mb-3">
            <Form.Label className="small text-muted">{labelTipoVideo}</Form.Label>
            <Form.Select value={tipoVideo} onChange={e => setTipoVideo(e.target.value)} required>
              {tipos.map(t => <option key={t} value={t}>{t}</option>)}
            </Form.Select>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label className="small text-muted">Nome da Fase (Etapa)</Form.Label>
            <Form.Control type="text" placeholder="Ex: RASCUNHO, REVISÃO FINAL" value={etapa} onChange={e => setEtapa(e.target.value.toUpperCase())} required autoFocus />
          </Form.Group>

          <Form.Group>
            <Form.Label className="small text-muted">Peso no Bônus (%)</Form.Label>
            <InputGroup>
              <Form.Control type="number" placeholder="Ex: 30" value={percentual} onChange={e => setPercentual(e.target.value)} required />
              <InputGroup.Text>%</InputGroup.Text>
            </InputGroup>
          </Form.Group>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-secondary" onClick={handleClose}>Cancelar</Button>
        <Button variant="primary" onClick={onSave}><i className="bi bi-save me-1"></i> Confirmar</Button>
      </Modal.Footer>
    </Modal>
  );
};

export default Configuracoes;